import { useApi } from '~/api'
import { defineService } from '~/api/models/helpers'
import type { AuthArgs } from '~/api/models/index'
import { useAuthStore } from '~/stores/authStore'
import type {
  ServiceDefinitionInput,
  ServiceDefinitionsQuery,
  ServiceDrillRequest,
  ServiceDuplicateInput,
  ServiceQueryRequest,
  ServiceRunRequest,
  ServiceRunsQuery,
  ServiceScheduleInput
} from '~/types/report-service'

export type QueryLicenceScope = number | 'none'

export const activeQueryLicenceScope = (): QueryLicenceScope => useAuthStore().activeLicence?.id ?? 'none'

export const REPORT_RUNS_QUERY_KEY = 'reportServiceRuns'
export const REPORT_SCHEDULES_QUERY_KEY = 'reportServiceSchedules'
export const REPORT_SCHEDULE_QUERY_KEY = 'reportServiceSchedule'
export const REPORT_CATALOG_QUERY_KEY = 'reportServiceCatalog'
export const REPORT_LIMITS_QUERY_KEY = 'reportServiceLimits'
export const REPORT_DEFINITIONS_QUERY_KEY = 'reportServiceDefinitions'
export const REPORT_DEFINITION_QUERY_KEY = 'reportServiceDefinition'
export const REPORT_CAPABILITIES_QUERY_KEY = 'reportServiceCapabilities'

export const reportCapabilitiesQueryKey = (scope: QueryLicenceScope): (string | number)[] => [
  REPORT_CAPABILITIES_QUERY_KEY,
  scope
]

const queries = {
  getCatalog: {
    queryFn: (args: Ref<{ locale: string }>) => useApi().reportService.getCatalog(args.value.locale),
    queryKey: (args: Ref<{ locale: string }>) => [
      REPORT_CATALOG_QUERY_KEY,
      activeQueryLicenceScope(),
      args.value.locale
    ],
    options: {
      staleTime: () => 1000 * 60 * 30
    }
  },
  getCapabilities: {
    queryFn: () => useApi().reportService.getCapabilities(),
    queryKey: () => reportCapabilitiesQueryKey(activeQueryLicenceScope()),
    options: {
      staleTime: () => 1000 * 60 * 5,
      retry: () => 0
    }
  },
  getLimits: {
    queryFn: () => useApi().reportService.getLimits(),
    queryKey: () => [REPORT_LIMITS_QUERY_KEY, activeQueryLicenceScope()],
    options: {
      staleTime: () => 1000 * 60 * 30,
      retry: () => 0
    }
  },
  getDefinitions: {
    queryFn: (args: Ref<ServiceDefinitionsQuery>) => useApi().reportService.listDefinitions(args.value),
    queryKey: (args: Ref<ServiceDefinitionsQuery>) => [
      REPORT_DEFINITIONS_QUERY_KEY,
      activeQueryLicenceScope(),
      { include: args.value.include, source: args.value.source }
    ],
    options: {
      staleTime: () => 1000 * 30
    }
  },
  getDefinition: {
    queryFn: (args: Ref<{ id: string }>) => useApi().reportService.getDefinition(args.value.id),
    queryKey: (args: Ref<{ id: string }>) => [REPORT_DEFINITION_QUERY_KEY, activeQueryLicenceScope(), args.value.id],
    options: {
      staleTime: () => 1000 * 30
    }
  },
  getRuns: {
    queryFn: (args: Ref<ServiceRunsQuery>) => useApi().reportService.listRuns(args.value),
    queryKey: (args: Ref<ServiceRunsQuery>) => [
      REPORT_RUNS_QUERY_KEY,
      { definitionId: args.value.definitionId, presetKey: args.value.presetKey, limit: args.value.limit }
    ],
    options: {
      staleTime: () => 0
    }
  },
  getRun: {
    queryFn: (args: Ref<{ id: string }>) => useApi().reportService.getRun(args.value.id),
    queryKey: (args: Ref<{ id: string }>) => [REPORT_RUNS_QUERY_KEY, args.value.id],
    options: {
      staleTime: () => 0
    }
  },
  getSchedules: {
    queryFn: () => useApi().reportService.listSchedules(),
    queryKey: () => [REPORT_SCHEDULES_QUERY_KEY],
    options: {
      staleTime: () => 1000 * 60
    }
  },
  getSchedule: {
    queryFn: (args: Ref<{ id: string }>) => useApi().reportService.getSchedule(args.value.id),
    queryKey: (args: Ref<{ id: string }>) => [REPORT_SCHEDULE_QUERY_KEY, args.value.id],
    options: {
      staleTime: () => 1000 * 60
    }
  }
} as const

const mutations = {
  createDefinition: {
    request: (input: ServiceDefinitionInput) => useApi().reportService.createDefinition(input),
    cacheKey: () => [[REPORT_DEFINITIONS_QUERY_KEY]]
  },
  updateDefinition: {
    request: (args: { id: string; input: ServiceDefinitionInput }) =>
      useApi().reportService.updateDefinition(args.id, args.input),
    cacheKey: (args: { id: string; input: ServiceDefinitionInput }) => [
      [REPORT_DEFINITIONS_QUERY_KEY],
      [REPORT_DEFINITION_QUERY_KEY, args.id]
    ]
  },
  deleteDefinition: {
    request: (id: string) => useApi().reportService.deleteDefinition(id),
    cacheKey: (id: string) => [[REPORT_DEFINITIONS_QUERY_KEY], [REPORT_DEFINITION_QUERY_KEY, id]]
  },
  duplicateFromPreset: {
    request: (args: { key: string; input: ServiceDuplicateInput }) =>
      useApi().reportService.duplicateFromPreset(args.key, args.input),
    cacheKey: () => [[REPORT_DEFINITIONS_QUERY_KEY]]
  },
  createRun: {
    request: (request: ServiceRunRequest) => useApi().reportService.createRun(request),
    cacheKey: () => [[REPORT_RUNS_QUERY_KEY]]
  },
  cancelRun: {
    request: (id: string) => useApi().reportService.cancelRun(id),
    cacheKey: () => [[REPORT_RUNS_QUERY_KEY]]
  },
  createSchedule: {
    request: (input: ServiceScheduleInput) => useApi().reportService.createSchedule(input),
    cacheKey: () => [[REPORT_SCHEDULES_QUERY_KEY]]
  },
  updateSchedule: {
    request: (args: { id: string; input: ServiceScheduleInput }) =>
      useApi().reportService.updateSchedule(args.id, args.input),
    cacheKey: (args: { id: string; input: ServiceScheduleInput }) => [
      [REPORT_SCHEDULES_QUERY_KEY],
      [REPORT_SCHEDULE_QUERY_KEY, args.id]
    ]
  },
  deleteSchedule: {
    request: (id: string) => useApi().reportService.deleteSchedule(id),
    cacheKey: (id: string) => [[REPORT_SCHEDULES_QUERY_KEY], [REPORT_SCHEDULE_QUERY_KEY, id]]
  },
  runScheduleNow: {
    request: (id: string) => useApi().reportService.runScheduleNow(id),
    cacheKey: (id: string) => [[REPORT_RUNS_QUERY_KEY], [REPORT_SCHEDULE_QUERY_KEY, id]]
  },
  runQuery: {
    request: (args: { request: ServiceQueryRequest; locale: string }) =>
      useApi().reportService.query(args.request, args.locale),
    cacheKey: () => []
  },
  drill: {
    request: (args: { request: ServiceDrillRequest; locale: string }) =>
      useApi().reportService.drill(args.request, args.locale),
    cacheKey: () => []
  }
} as const

export type ReportServiceModelService = ReturnType<typeof defineService<typeof queries, typeof mutations>>

export const registerService = (authArgs: AuthArgs): ReportServiceModelService =>
  defineService(authArgs, {
    queries,
    mutations
  })
